// k), not a free-text name.

import type { ManifestLayer } from "../types/contract";
import { pointInPolygons, polygonsOf } from "./samplingDesign";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

export interface ZoneCorePoint {
  lng: number;
  lat: number;
}

export interface ZoneCore extends ZoneCorePoint {
  zone: number;
}

/* * 1-based feature order of the first zone polygon that contains the point, or null. */
export function zoneNumberAt(
  fc: GeoJSON.FeatureCollection | null | undefined,
  lng: number,
  lat: number,
): number | null {
  const features = fc?.features ?? [];
  for (let i = 0; i < features.length; i++) {
    const polys = polygonsOf(features[i]!);
    if (polys.length && pointInPolygons({ lng, lat }, polys)) return i + 1;
  }
  return null;
}

/* * Cores still inside the zones that will be saved, numbered by that feature order. */
export function coresForSave(
  fc: GeoJSON.FeatureCollection | null | undefined,
  points: ZoneCorePoint[],
): ZoneCore[] {
  const out: ZoneCore[] = [];
  for (const p of points) {
    const zone = zoneNumberAt(fc, p.lng, p.lat);
    if (zone != null) out.push({ lng: p.lng, lat: p.lat, zone });
  }
  return out;
}

export function nearestCoreIndex(
  points: ZoneCorePoint[],
  lng: number,
  lat: number,
  maxM = 12,
): number {
  const kx = METRES_PER_DEGREE_LNG * Math.cos((lat * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  let best = -1;
  let bestD = maxM * maxM;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const dx = (p.lng - lng) * kx;
    const dy = (p.lat - lat) * ky;
    const d = dx * dx + dy * dy;
    if (d <= bestD) {
      best = i;
      bestD = d;
    }
  }
  return best;
}

export function zoneCoresFeatureCollection(
  cores: ZoneCore[],
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: cores.map((c, i) => ({
      type: "Feature",
      properties: { i, zone: c.zone, zone_number: c.zone },
      geometry: { type: "Point", coordinates: [c.lng, c.lat] },
    })),
  };
}

/* * Every draft dot, including one being dragged outside a zone. `zone` updates as it crosses a boundary. */
export function draftCoresFeatureCollection(
  fc: GeoJSON.FeatureCollection | null | undefined,
  points: ZoneCorePoint[],
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: points.map((p, i) => {
      const zone = zoneNumberAt(fc, p.lng, p.lat);
      return {
        type: "Feature",
        properties: { i, zone: zone ?? "", zone_number: zone ?? "" },
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
      };
    }),
  };
}

export function zoneCoresLayerId(versionId: string): string {
  return `zone-cores-${versionId}`;
}

/* * 0 → "Historical Cores A", 1 → "Historical Cores B", 26 → "Historical Cores AA". */
export function historicalCoreLabel(index: number): string {
  let n = index + 1;
  let letters = "";
  while (n > 0) {
    n -= 1;
    letters = String.fromCharCode(65 + (n % 26)) + letters;
    n = Math.floor(n / 26);
  }
  return `Historical Cores ${letters}`;
}

function pointsOfInline(inline: unknown): ZoneCorePoint[] {
  const features = (inline as { features?: GeoJSON.Feature[] } | null)
    ?.features;
  if (!Array.isArray(features)) return [];
  const out: ZoneCorePoint[] = [];
  for (const ft of features) {
    const g = ft?.geometry;
    if (!g) continue;
    if (g.type === "Point") {
      const [lng, lat] = g.coordinates;
      if (Number.isFinite(lng) && Number.isFinite(lat)) out.push({ lng, lat });
    } else if (g.type === "MultiPoint") {
      for (const c of g.coordinates) {
        const [lng, lat] = c;
        if (Number.isFinite(lng) && Number.isFinite(lat))
          out.push({ lng, lat });
      }
    }
  }
  return out;
}

export interface HistoricalZoneCores {
  id: string;
  label: string;
  points: ZoneCorePoint[];
}

/*
 *
 * Saved zone-core layers for the caret, newest first. `extra` are client copies
 * the manifest has not returned yet; a copy whose id is already served is dropped.
 */
export function historicalZoneCores(
  manifest: { id?: string; category?: string; inline?: unknown }[],
  extra: { id?: string; category?: string; inline?: unknown }[] = [],
): HistoricalZoneCores[] {
  const served = manifest.filter((l) => isZoneCoresLayer(l));
  const servedIds = new Set(served.map((l) => l.id));
  const pending = extra.filter(
    (l) => l.id && isZoneCoresLayer(l) && !servedIds.has(l.id),
  );
  return [...pending, ...served]
    .flatMap((layer) => {
      const points = pointsOfInline(layer.inline);
      if (!layer.id || !points.length) return [];
      return [{ id: layer.id, points }];
    })
    .map((row, i) => ({ ...row, label: historicalCoreLabel(i) }));
}

export function isZoneCoresLayer(layer: {
  id?: string;
  category?: string;
}): boolean {
  return (
    layer.category === "zone_cores" ||
    (layer.id ?? "").startsWith("zone-cores-")
  );
}

export function buildZoneCoreLayers(
  sets: { id: string; createdAt: string; cores: ZoneCore[] }[],
): ManifestLayer[] {
  return sets
    .filter((s) => s.cores.length)
    .map((s, i) => ({
      id: zoneCoresLayerId(s.id),
      kind: "geojson" as const,
      title: "Zone Cores",
      category: "zone_cores",
      inline: zoneCoresFeatureCollection(s.cores),
      default_visible: i === 0,
      default_opacity: 1,
      order: 206,
      content_version: s.createdAt,
      is_stale: false,
    }));
}
